import type { JsonSchema } from '@southfields-digital/mpxlive-components';
import { useCallback, useEffect, useReducer, useRef } from 'react';

import { getRuntimeSchema } from '../api/dataConnectorApi';
import type { RuntimeSchemaResponse } from '../types';

type UseRuntimeSchemaParams = {
  configFlowId: string | undefined;
  designTimeContext: EndpointFlowState;
};

type State =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: RuntimeSchemaResponse }
  | { status: 'error'; error: string };

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; data: RuntimeSchemaResponse }
  | { type: 'FETCH_ERROR'; error: string }
  | { type: 'RESET' };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'FETCH_START':
      return { status: 'loading' };
    case 'FETCH_SUCCESS':
      return { status: 'success', data: action.data };
    case 'FETCH_ERROR':
      return { status: 'error', error: action.error };
    case 'RESET':
      return { status: 'idle' };
    default:
      return state;
  }
};

type UseRuntimeSchemaResult = {
  isLoading: boolean;
  error: string | undefined;
  schema: JsonSchema | undefined;
  formData: EndpointFlowState | undefined;
  refetch: () => void;
};

export const useRuntimeSchema = (params: UseRuntimeSchemaParams): UseRuntimeSchemaResult => {
  const { configFlowId, designTimeContext } = params;

  const [state, dispatch] = useReducer(reducer, { status: 'idle' });
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchSchema = useCallback(async () => {
    if (!configFlowId) {
      dispatch({ type: 'RESET' });
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    dispatch({ type: 'FETCH_START' });

    try {
      const response = await getRuntimeSchema({
        configFlowId,
        state: designTimeContext,
        signal: controller.signal,
      });

      if (controller.signal.aborted) {
        return;
      }

      dispatch({ type: 'FETCH_SUCCESS', data: response });
    } catch (err) {
      if (controller.signal.aborted || (err instanceof Error && err.name === 'AbortError')) {
        return;
      }

      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch runtime schema';
      dispatch({ type: 'FETCH_ERROR', error: errorMessage });
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [configFlowId, designTimeContext]);

  useEffect(() => {
    if (!configFlowId) {
      dispatch({ type: 'RESET' });
      return;
    }

    void fetchSchema();

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [configFlowId, fetchSchema]);

  const refetch = useCallback(() => {
    void fetchSchema();
  }, [fetchSchema]);

  return {
    isLoading: state.status === 'loading',
    error: state.status === 'error' ? state.error : undefined,
    schema: state.status === 'success' ? state.data.schema : undefined,
    formData: state.status === 'success' ? state.data.formData : undefined,
    refetch,
  };
};
